import React, { useContext } from 'react'
import { ComponentConfig, Slot, renderContext } from '@puckeditor/core'
import PuckPollContext from 'breaktime-theme/src/steps/poll/components/PuckPollContext'
import { PollProducts } from 'breaktime-theme/src/steps/poll/components/PollProducts'
import {
  TypographyProps,
  fontFamilyField,
  fontFamilyFallbackField,
  textTransformField,
  fontWeightOptions,
  applyTypography,
  FontWeight,
} from 'breaktime-theme/src/puck/fields/StyleFields'
import { ColorField } from 'breaktime-theme/src/puck/fields/ColorField'

export type PollProductPanelProps = {
  headline?: string
  products: Slot
  fontFamily?: TypographyProps['fontFamily']
  fontFamilyFallback?: TypographyProps['fontFamilyFallback']
  textTransform?: TypographyProps['textTransform']
  fontSize?: string
  fontWeight?: FontWeight
  color?: string
}

export const POLL_PRODUCT_PANEL_DEFAULT_PROPS: PollProductPanelProps = {
  headline: 'Shop Products',
  products: [],
}

const buildHeadlineStyle = ({
  fontFamily,
  fontFamilyFallback,
  textTransform,
  fontSize,
  fontWeight,
  color,
}: PollProductPanelProps): React.CSSProperties => ({
  ...applyTypography({ fontFamily, fontFamilyFallback, textTransform }),
  ...(fontSize ? { fontSize } : {}),
  ...(fontWeight ? { fontWeight: Number(fontWeight) || undefined } : {}),
  ...(color ? { color } : {}),
})

const PollProductPanelRender = (
  props: PollProductPanelProps & { products: any },
) => {
  const { headline, products: Products } = props
  const puckCtx = useContext(renderContext)
  const pollCtx = useContext(PuckPollContext)
  const isRenderMode = puckCtx.data.content.length > 0
  const headlineStyle = buildHeadlineStyle(props)

  if (isRenderMode) {
    if (!pollCtx.products?.length) return null

    return (
      <PollProducts
        title={headline || 'Shop Products'}
        titleStyle={headlineStyle}
        products={pollCtx.products}
        question={pollCtx.question}
        onAnswerDropdownClick={pollCtx.onAnswerDropdownClick}
        onAnswerLinkClick={pollCtx.onAnswerLinkClick}
        poll={pollCtx.poll}
      />
    )
  }

  // Editor mode
  return (
    <div className="poll-products">
      <h3 style={headlineStyle}>{headline || 'Shop Products'}</h3>
      <Products
        allow={['PollProduct']}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}
      />
    </div>
  )
}

export const PuckPollProductPanel: ComponentConfig<PollProductPanelProps> = {
  label: 'Poll Product Panel',
  defaultProps: POLL_PRODUCT_PANEL_DEFAULT_PROPS,
  fields: {
    headline: {
      type: 'text',
      label: 'Headline',
    },
    products: {
      type: 'slot',
      allow: ['PollProduct'],
    },
    fontFamily: fontFamilyField,
    fontFamilyFallback: fontFamilyFallbackField,
    textTransform: textTransformField,
    fontSize: { type: 'text', label: 'Font Size', placeholder: '20px' },
    fontWeight: { type: 'select', label: 'Font Weight', options: fontWeightOptions },
    color: {
      type: 'custom',
      label: 'Color',
      render: (props: any) => <ColorField {...props} clearable />,
    },
  },
  render: props => <PollProductPanelRender {...props} />,
}
